import { ILease } from "./PA.UtilsIF";
import * as AsyncLock from "async-lock";
import { EntityTransformRequest, IHierarchyTransformService } from "../PA.MD.Hierarchy/HierarachyTransformService";
import { DateUtils } from "../PA.Core/Basic/Dates";

export class CustomerHierarchyContext {
    private readonly custHierWbID: number;
    private readonly wbSalesOrgId: number;
    private readonly wbMdSetId: number;
    private dctLock: AsyncLock = new AsyncLock();
    private readonly hierarchyTransformService: IHierarchyTransformService;

    public get WbMdSetId(): number {
        return this.wbMdSetId;
    }

    private transformationCache: { [key: string]: number[] } = {};

    private readonly _lease: ILease;

    /**
     * @param custHWbID The WB_CustH_ID of the hierarchy. Also in the WB_Hierarchy.Target_WB_ID field
     * @param wbSalesOrgId provides the SalesOrgId for the hierarchy's traversal requests
     * @param transformService Executes hierarchy transformations
     * */
    constructor(
        custHWbID: number,
        wbSalesOrgId: number,
        wbMdSetId: number,
        lease: ILease,
        transformService: IHierarchyTransformService,
    ) {
        this.custHierWbID = custHWbID;
        this.wbSalesOrgId = wbSalesOrgId;
        this.wbMdSetId = wbMdSetId;
        this.hierarchyTransformService = transformService;
        this._lease = lease;
    }

    private getKey(
        sourceWbEntityId: number,
        targetWbEntityId: number,
        sourceWdId: number,
        effectiveDate: Date,
    ): string {
        return `${sourceWbEntityId}_${targetWbEntityId}_${sourceWdId}_${effectiveDate.toISOString()}`;
    }

    /**
     * The transform method will return an array of WB_IDs that are the result of the transformation of the sourceWdId to the targetWbEntityId
     * for the effective date provided.
     * @param sourceWbEntityId The WB_ID of the source entity from which the transformation will be executed
     * @param targetWbEntityId The WB_ID of the target entity to which the transformation will be executed
     * @param sourceWdId The WB_ID of the source entity to be transformed
     * @param effectiveDate The effective date for the transformation
     * */
    async transform(
        sourceWbEntityId: number,
        targetWbEntityId: number,
        sourceWdId: number,
        effectiveDate: Date,
    ): Promise<number[]> {
        const self = this;
        return self.dctLock.acquire("global", async () => {
            await self.checkLease();

            const key = self.getKey(sourceWbEntityId, targetWbEntityId, sourceWdId, effectiveDate);
            if (key in self.transformationCache) {
                return self.transformationCache[key];
            }

            const request: EntityTransformRequest = {
                wbsalesOrgId: this.wbSalesOrgId,
                wbhierarchyTargetId: this.custHierWbID,
                entityDimension: "Customer",
                wbentityIdSource: sourceWbEntityId,
                wbentityIdTarget: targetWbEntityId,
                sourceWBIds: [sourceWdId],
                effectiveDate: DateUtils.convertToSAPBW(effectiveDate),
            };

            const response = await this.hierarchyTransformService.getEntityTransform(request);
            if (!response.mappings || !(sourceWdId in response.mappings)) {
                self.transformationCache[key] = [];
                return [];
            }
            const vals = response.mappings[sourceWdId].map((mapping) => mapping.wbId);
            self.transformationCache[key] = vals;
            return vals;
        });
    }

    //<editor-fold desc="Helper methods">

    async reset(): Promise<void> {
        this._lease.forceExpire();
        this.transformationCache = {};
    }

    //</editor-fold>

    //<editor-fold desc="Leasing">

    private async checkLease(): Promise<void> {
        if (await this._lease.hasDataChanged()) {
            await this.reset();
        }
    }

    //</editor-fold>
}
